/**
 * Réactions « tapback » dans les exports SMS MonLivreSMS (app Android / logiciel iOS).
 *
 * Certaines messageries recopient une réaction (❤️, 👍, …) comme un pseudo-message texte :
 *   "❤️ à \"Coucou belle ALEX\""   (FR)
 *   "❤️ to \"Hello\""              (EN)
 *   "❤️ à une photo"               (réaction sur une pièce jointe)
 *
 * Le marqueur `\u200a\u200b` (hair space + zero-width space) est injecté par l'export
 * MonLivreSMS — présent sur Android comme sur iOS.
 *
 * Sans traitement, ce pseudo-message recopie tout le texte d'origine dans une bulle.
 * Ici on :
 *   1. Détecte le tapback via ce marqueur (signal fiable, zéro faux positif),
 *   2. Retrouve le message d'origine (citation exacte, sous-chaîne, ou pièce jointe),
 *   3. Rattache l'emoji au message cible (message.reactions) façon iMessage,
 *   4. Marque le pseudo-message (isTapbackSource) pour l'exclure de la pagination livre.
 *
 * IMPORTANT (anti-régression) :
 *   - Sans le marqueur \u200a\u200b, aucun message n'est touché (👍 seul, guillemets, etc.).
 *   - WhatsApp / Instagram / Messenger : réactions filtrées à l'import provider (hors CSV SMS).
 *   - Idempotent : les sources déjà marquées isTapbackSource sont ignorées.
 */

// Marqueur invisible en tête des tapbacks export MonLivreSMS (Android / iOS).
// Version encodée telle que stockée dans le CSV (message.text est URL-encodé).
const IOS_TAPBACK_MARKER = '\u200a\u200b';
const IOS_TAPBACK_MARKER_ENCODED = '%E2%80%8A%E2%80%8B';

// Espaces invisibles / marques directionnelles à retirer avant parsing et matching.
// eslint-disable-next-line no-misleading-character-class
const INVISIBLE_CHARS_REGEX = /[\u200a\u200b\u200c\u200d\u200e\u200f\ufeff]/g;

// Connecteurs (mot entre l'emoji et la cible) selon la langue de la messagerie.
// FR: à — EN: to — ES/IT/PT: a/al/ad/à — DE: auf/an — NL: op/aan
const TAPBACK_CONNECTORS = ['à', 'to', 'al', 'aan', 'auf', 'an', 'ad', 'a', 'op'];

// Formulations "réaction sur média" (sans citation) par langue.
const TAPBACK_MEDIA_PHRASES = {
	photo: ['une photo', 'a photo', 'an image', 'una foto', 'ein foto', 'een foto', 'uma foto'],
	video: ['une vidéo', 'a video', 'un vídeo', 'ein video', 'un video', 'een video', 'um vídeo'],
};

function escapeRegExp(text) {
	return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Connecteurs triés du plus long au plus court pour éviter qu'un connecteur court
// (ex. "a") ne "mange" un connecteur long (ex. "aan") dans l'alternance regex.
const CONNECTOR_ALTERNATION = [...TAPBACK_CONNECTORS]
	.sort((a, b) => b.length - a.length)
	.map(escapeRegExp)
	.join('|');

// EMOJI (groupe non alphabétique) + connecteur + reste (citation ou média).
// Le groupe 1 est non-gourmand → s'arrête au premier connecteur rencontré.
const TAPBACK_REGEX = new RegExp(`^(\\S[\\s\\S]*?)\\s+(?:${CONNECTOR_ALTERNATION})\\s+([\\s\\S]+)$`, 'i');

function safeDecode(text) {
	try {
		return decodeURIComponent(text);
	} catch (err) {
		return text;
	}
}

/**
 * Normalise un texte pour la comparaison message ↔ citation :
 * décode, retire les invisibles, unifie apostrophes/guillemets typographiques,
 * réduit les espaces. Ne modifie jamais le message stocké.
 */
export function normalizeForMatch(text) {
	if(!text) {
		return '';
	}
	return safeDecode(String(text))
		.replace(INVISIBLE_CHARS_REGEX, '')
		.replace(/[\u2018\u2019\u02bc]/g, "'")
		.replace(/[\u201c\u201d\u00ab\u00bb]/g, '"')
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * Parse un texte de message brut (URL-encodé ou non) en tapback iOS.
 * Retourne { emoji, quotedText? , mediaType? } ou null si ce n'est pas un tapback.
 *
 * Détection stricte : nécessite le marqueur invisible iOS. Un message normal ou
 * un simple emoji ne sont donc jamais détectés comme réaction.
 */
export function parseIosTapback(rawText) {
	if(!rawText || typeof rawText !== 'string') {
		return null;
	}
	// Pré-filtre rapide sur le texte brut (encodé) pour éviter de décoder tous les messages.
	if(rawText.indexOf(IOS_TAPBACK_MARKER_ENCODED) === -1 && rawText.indexOf(IOS_TAPBACK_MARKER) === -1) {
		return null;
	}
	const decoded = safeDecode(rawText);
	if(decoded.indexOf(IOS_TAPBACK_MARKER) === -1) {
		return null;
	}

	const cleaned = decoded.replace(INVISIBLE_CHARS_REGEX, '').trim();
	const match = cleaned.match(TAPBACK_REGEX);
	if(!match) {
		return null;
	}

	const emoji = match[1].trim();
	const rest = match[2].trim();

	// L'emoji ne doit contenir aucune lettre/chiffre (sinon c'est du texte, pas une réaction).
	if(!emoji || /[\p{L}\p{N}]/u.test(emoji)) {
		return null;
	}

	// Cible = citation entre guillemets.
	const quotedMatch = rest.match(/^["“«]([\s\S]*)["”»]$/);
	if(quotedMatch) {
		return { emoji, quotedText: quotedMatch[1].trim() };
	}

	// Cible = média (photo / vidéo).
	const restLower = rest.toLowerCase();
	if(TAPBACK_MEDIA_PHRASES.photo.includes(restLower)) {
		return { emoji, mediaType: 'photo' };
	}
	if(TAPBACK_MEDIA_PHRASES.video.includes(restLower)) {
		return { emoji, mediaType: 'video' };
	}

	// Citation sans guillemets (rare) — best effort, on est déjà certain du marqueur iOS.
	return { emoji, quotedText: rest };
}

const SUBSTRING_SEARCH_WINDOW = 80; // messages remontés pour le repli sous-chaîne
const MEDIA_SEARCH_WINDOW = 50; // messages remontés pour retrouver la PJ réagie

/**
 * Retrouve l'index du message cible d'un tapback (le plus proche AVANT la réaction).
 * Retourne -1 si aucune cible fiable.
 */
function findTapbackTargetIndex(messages, parsed, reactionIndex, tapback, textIndex) {
	if(tapback.quotedText) {
		const normalized = normalizeForMatch(tapback.quotedText);
		if(normalized) {
			// 1. Correspondance exacte (index O(1)) → dernier message avant la réaction.
			const exact = (textIndex.get(normalized) || []).filter(idx => idx < reactionIndex);
			if(exact.length) {
				return exact[exact.length - 1];
			}
			// 2. Repli sous-chaîne bidirectionnelle sur une fenêtre glissante.
			let seen = 0;
			for(let j = reactionIndex - 1; j >= 0 && seen < SUBSTRING_SEARCH_WINDOW; j -= 1) {
				// Ne jamais cibler un autre tapback (parsed[j] non nul).
				if(!parsed[j]) {
					const candidate = normalizeForMatch(messages[j].text || '');
					if(candidate) {
						seen += 1;
						if(candidate.includes(normalized) || normalized.includes(candidate)) {
							return j;
						}
					}
				}
			}
		}
	}

	if(tapback.mediaType) {
		let seen = 0;
		for(let j = reactionIndex - 1; j >= 0 && seen < MEDIA_SEARCH_WINDOW; j -= 1) {
			if(!parsed[j]) {
				if((messages[j].attachments || []).length) {
					return j;
				}
				seen += 1;
			}
		}
	}

	return -1;
}

/**
 * Fusionne les réactions tapback iOS d'un tableau de messages (ordre chronologique attendu).
 *
 * - Rattache chaque emoji à son message d'origine via message.reactions[].
 * - Marque le pseudo-message tapback : isTapbackSource (exclu du livre en aval).
 * - Si aucune cible : repli en bulle emoji seule (isTapbackFallback, texte = emoji).
 * - Aucun tapback détecté : renvoie le tableau d'origine inchangé (aucune copie).
 */
export function mergeTapbackReactions(messages) {
	if(!Array.isArray(messages) || messages.length === 0) {
		return messages;
	}

	// Parse chaque message. Les sources déjà fusionnées (isTapbackSource) sont ignorées
	// → idempotence (pas de double rattachement si la fonction est rappelée).
	const parsed = messages.map(message => (
		message && !message.isTapbackSource ? parseIosTapback(message.text) : null
	));

	if(!parsed.some(Boolean)) {
		return messages; // conversation sans tapback (WhatsApp, etc.) : rien à faire
	}

	// Copie de travail (on n'altère jamais les objets d'origine).
	const result = messages.map(message => ({ ...message }));

	// Index texte normalisé → indices, sur les seuls messages non-tapback (cibles potentielles).
	const textIndex = new Map();
	for(let i = 0; i < result.length; i += 1) {
		if(!parsed[i]) {
			const normalized = normalizeForMatch(result[i].text || '');
			if(normalized) {
				if(!textIndex.has(normalized)) {
					textIndex.set(normalized, []);
				}
				textIndex.get(normalized).push(i);
			}
		}
	}

	for(let i = 0; i < result.length; i += 1) {
		const tapback = parsed[i];
		if(tapback) {
			const targetIndex = findTapbackTargetIndex(result, parsed, i, tapback, textIndex);
			if(targetIndex >= 0) {
				const target = result[targetIndex];
				target.reactions = [
					...(target.reactions || []),
					{
						emoji: tapback.emoji,
						sender: result[i].sender,
						date: result[i].date,
						reference: result[i].reference,
					},
				];
				result[i].isTapbackSource = true;
			} else {
				// Pas de cible fiable : bulle emoji seule (coût pagination minimal).
				result[i].isTapbackFallback = true;
				result[i].text = encodeURIComponent(tapback.emoji);
			}
		}
	}

	return result;
}
